import { useState, useRef, useEffect, useCallback, useMemo, type RefCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon, XMarkIcon, BuildingOffice2Icon, UserIcon } from '@heroicons/react/24/outline';
import { queryKeys } from '@/shared/api/queryKeys';
import { STALE_TIME_LONG } from '@/shared/constants/timing';
import { getOrganisations } from '@/features/organisations/api/organisationsApi';
import { getUsers } from '@/features/users/api/usersApi';
import { cn } from '@/shared/utils/cn';

interface Organisation {
  organisation_id: string;
  name: string;
  type?: string;
}

interface User {
  user_id: string;
  email: string;
  information?: {
    first_name?: string;
    last_name?: string;
  };
}

interface SearchResult {
  id: string;
  label: string;
  sublabel?: string;
  type: 'organisation' | 'user';
  path: string;
}

interface GlobalSearchProps {
  className?: string;
  variant?: 'icon' | 'input';
  placeholder?: string;
  searchType?: 'all' | 'organisations' | 'users';
}

export function GlobalSearch({
  className,
  variant = 'icon',
  placeholder = 'Search organisations & users...',
  searchType = 'all',
}: GlobalSearchProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const wantOrganisations = searchType === 'all' || searchType === 'organisations';
  const wantUsers = searchType === 'all' || searchType === 'users';

  const { data: orgIndex = [] } = useQuery({
    queryKey: queryKeys.search.organisations(),
    queryFn: async (): Promise<Organisation[]> => {
      const res = await getOrganisations({});
      return (res.data?.organisations ?? []).map((o) => ({
        organisation_id: o.organisation_id,
        name: o.name,
        type: o.type,
      }));
    },
    enabled: wantOrganisations,
    staleTime: STALE_TIME_LONG,
  });

  const { data: userIndex = [] } = useQuery({
    queryKey: queryKeys.search.users(),
    queryFn: async (): Promise<User[]> => {
      const res = await getUsers({});
      return (res.data?.users ?? []).map((u) => ({
        user_id: u.user_id,
        email: u.email,
        information: {
          first_name: u.information?.first_name,
          last_name: u.information?.last_name,
        },
      }));
    },
    enabled: wantUsers,
    staleTime: STALE_TIME_LONG,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const highlightedRef: RefCallback<HTMLButtonElement> = useCallback((node) => {
    node?.scrollIntoView({ block: 'nearest' });
  }, []);

  const results = useMemo((): SearchResult[] => {
    if (search.length < 3) return [];

    const searchLower = search.toLowerCase();
    const results: SearchResult[] = [];

    if (wantOrganisations) {
      orgIndex.forEach((org) => {
        if (
          org.name?.toLowerCase().includes(searchLower) ||
          org.organisation_id?.toLowerCase().includes(searchLower)
        ) {
          results.push({
            id: org.organisation_id,
            label: org.name,
            sublabel: org.type,
            type: 'organisation',
            path: `/organisations/${org.organisation_id}/company-info`,
          });
        }
      });
    }

    if (wantUsers) {
      userIndex.forEach((user) => {
        const fullName = [user.information?.first_name, user.information?.last_name]
          .filter(Boolean)
          .join(' ');

        if (
          user.email?.toLowerCase().includes(searchLower) ||
          fullName.toLowerCase().includes(searchLower) ||
          user.user_id?.toLowerCase().includes(searchLower)
        ) {
          results.push({
            id: user.user_id,
            label: user.email,
            sublabel: fullName || undefined,
            type: 'user',
            path: `/users/${user.user_id}/user-info`,
          });
        }
      });
    }

    // Limit results
    return results.slice(0, 10);
  }, [search, wantOrganisations, wantUsers, orgIndex, userIndex]);

  const groupedResults = useMemo(() => {
    const groups: { organisations: SearchResult[]; users: SearchResult[] } = {
      organisations: [],
      users: [],
    };

    results.forEach((result) => {
      if (result.type === 'organisation') {
        groups.organisations.push(result);
      } else {
        groups.users.push(result);
      }
    });

    return groups;
  }, [results]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  };

  const handleSelect = (result: SearchResult) => {
    // Invalidate the detail queries to ensure fresh data is fetched
    if (result.type === 'organisation') {
      queryClient.invalidateQueries({ queryKey: queryKeys.organisations.detail(result.id) });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(result.id) });
    }

    navigate(result.path);
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightedIndex]);
    }
  };

  if (variant === 'icon' && !isOpen) {
    return (
      <button
        onClick={handleOpen}
        className={cn(
          'p-1.5 rounded-md text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/50 transition-colors',
          className
        )}
        aria-label="Search"
      >
        <MagnifyingGlassIcon className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          aria-label="Search organisations and users"
          className={cn(
            'w-full pl-9 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500'
          )}
        />
        {(search || variant === 'icon') && (
          <button
            onClick={handleClose}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
            aria-label="Clear search"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && search.length >= 3 && (
        <div className="absolute z-50 mt-1 w-full min-w-[300px] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No results found for "{search}"
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {groupedResults.organisations.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <BuildingOffice2Icon className="h-3.5 w-3.5" />
                    Organisations
                  </div>
                  {groupedResults.organisations.map((result) => {
                    const index = results.indexOf(result);
                    const isHighlighted = index === highlightedIndex;
                    return (
                      <button
                        key={result.id}
                        ref={isHighlighted ? highlightedRef : undefined}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          'w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                          isHighlighted && 'bg-primary-50 dark:bg-primary-900/50'
                        )}
                      >
                        <BuildingOffice2Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {result.label}
                          </div>
                          {result.sublabel && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                              {result.sublabel}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {groupedResults.users.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <UserIcon className="h-3.5 w-3.5" />
                    Users
                  </div>
                  {groupedResults.users.map((result) => {
                    const index = results.indexOf(result);
                    const isHighlighted = index === highlightedIndex;
                    return (
                      <button
                        key={result.id}
                        ref={isHighlighted ? highlightedRef : undefined}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          'w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                          isHighlighted && 'bg-primary-50 dark:bg-primary-900/50'
                        )}
                      >
                        <UserIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {result.label}
                          </div>
                          {result.sublabel && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {result.sublabel}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border-t border-gray-100 dark:border-gray-600 text-xs text-gray-400">
            Use <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-300">↑</kbd>{' '}
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-300">↓</kbd> to navigate,{' '}
            <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-300">Enter</kbd> to select
          </div>
        </div>
      )}

      {/* Hint when less than 3 chars */}
      {isOpen && search.length > 0 && search.length < 3 && (
        <div className="absolute z-50 mt-1 w-full min-w-[300px] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
          Type at least 3 characters to search...
        </div>
      )}
    </div>
  );
}
